import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { getApiUrl } from '../utils/apiConfig';
import Header from '../components/common/Header';
import PasswordInput from '../components/common/PasswordInput';
import '../styles/ResetPassword.css';

const ResetPassword = () => {
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const resetButtons = [
    { id: 1, label: 'Login', path: '/login' },
    { id: 2, label: 'Sign Up', path: '/signup' }
  ];

  // Check if user is coming from password reset email link
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');

    if (accessToken && type === 'recovery') {
      console.log('Password reset link detected');
      setIsResettingPassword(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Send password reset email via Supabase
      // Supabase will only send the email if the email exists in their system
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        formData.email,
        {
          redirectTo: window.location.origin + '/reset-password'
        }
      );

      if (resetError) {
        // Handle specific Supabase errors
        if (resetError.message.includes('not found') || resetError.message.includes('User not found')) {
          throw new Error('Email not found. Please check your email address.');
        }
        throw resetError;
      }

      // Show success message regardless (for security, don't confirm if email exists)
      setEmailSent(true);
      setError('');
    } catch (err) {
      console.error('Password reset request error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      // Update password in Supabase (user is authenticated via the reset link)
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (updateError) throw updateError;

      // Update password in backend as well
      const { data: { user } } = await supabase.auth.getUser();
      
      const updateResponse = await fetch(getApiUrl('update-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          newPassword: formData.newPassword
        })
      });

      if (!updateResponse.ok) {
        console.warn('Backend password update failed, but Supabase password updated successfully');
      }

      setSuccess(true);
      setError('');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);

    } catch (err) {
      console.error('Password update error:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If coming from password reset email, show password update form
  if (isResettingPassword) {
    return (
      <div className="reset-password-page">
        <Header buttons={resetButtons} className="reset-password-header" />
        <div className='reset-password-main'>
          <img
            src={process.env.PUBLIC_URL + '/ITZ.LogoTypeFinalwithTag_AvenirNext.Web.png'}
            alt="ITZ Main Logo"
            className="reset-password-logo"
          />
          <div className='reset-password-form-container'>
            <form onSubmit={handleUpdatePassword} className="reset-password-form">
              {error && (
                <div className="reset-password-error">
                  {error}
                </div>
              )}
              {success && (
                <div className="reset-password-success">
                  Password updated successfully! Redirecting to login...
                </div>
              )}
              
              <div className="reset-password-form-group">
                <label className="reset-password-label">New Password:</label>
                <PasswordInput
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  required
                  className="reset-password-input"
                  wrapClassName="reset-password-wrap"
                  placeholder="Enter new password"
                />
              </div>

              <div className="reset-password-form-group">
                <label className="reset-password-label">Confirm Password:</label>
                <PasswordInput
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  className={`reset-password-input${formData.confirmPassword && formData.confirmPassword !== formData.newPassword ? ' error' : ''}`}
                  wrapClassName="reset-password-wrap"
                  placeholder="Confirm new password"
                />
                {formData.confirmPassword && formData.confirmPassword !== formData.newPassword && (
                  <p className="reset-password-field-error">Passwords do not match</p>
                )}
              </div>

              <div className="reset-password-submit-row">
                <button type="submit" disabled={loading || success} className="reset-password-submit app-btn">
                  {loading ? 'Updating...' : success ? 'Success!' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Default: Show request reset form
  return (
    <div className="reset-password-page">
      <Header buttons={resetButtons} className="reset-password-header" />
      <div className='reset-password-main'>
        <img
          src={process.env.PUBLIC_URL + '/ITZ.LogoTypeFinalwithTag_AvenirNext.Web.png'}
          alt="ITZ Main Logo"
          className="reset-password-logo"
        />
        <div className='reset-password-form-container'>
          {emailSent ? (
            <div className="reset-password-email-sent">
              <h2>Check Your Email</h2>
              <p>We've sent a password reset link to <strong>{formData.email}</strong></p>
              <p>Click the link in the email to reset your password.</p>
              <p className="reset-password-note">The link will expire in 1 hour.</p>
            </div>
          ) : (
            <form onSubmit={handleRequestReset} className="reset-password-form">
              {error && (
                <div className="reset-password-error">
                  {error}
                </div>
              )}
              
              <div className="reset-password-instructions">
                <p className="reset-password-instructions-title"><strong>Forgot your password?</strong></p>
                <p className="reset-password-instructions-subheading">Fill in your email and click on Reset Password button.</p>
                <p className="reset-password-instructions-body">Check you email for your password reset link. Click on the link and you wil be brought to a new page to input a new password</p>
              </div>
              
              <div className="reset-password-form-group">
                <label className="reset-password-label">Email ID:</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="reset-password-input"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="reset-password-submit-row">
                <button type="submit" disabled={loading} className="reset-password-submit app-btn">
                  {loading ? 'Sending...' : 'Reset Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

